import { buildListVar, DecisionListItem, formatDecision } from './list-var';
import { sendToAdobe } from './send-to-adobe';

// Fixed grace window (ms) from the first buffered item; coalesces near-simultaneous decisions.
export const GRACE_MS = 200;

interface ForwardingBuffer {
	add(item: DecisionListItem): void;
}

declare global {
	interface Window {
		__fshrForwardingBuffer?: ForwardingBuffer;
	}
}

function createForwardingBuffer(): ForwardingBuffer {
	// Shared on `window` so dedup/coalescing stay unique across every remote/provider. Resets on reload.
	const sentAssignments = new Set<string>();
	const pending = new Map<string, DecisionListItem>();
	let timer: ReturnType<typeof setTimeout> | null = null;

	const flush = () => {
		timer = null;
		if (pending.size === 0) return;
		const experimentName = buildListVar([...pending.values()]);
		pending.clear();
		if (process.env['NODE_ENV'] !== 'production') {
			console.log('[AdobeForwarding] experiment_name:', experimentName);
		}
		// A forwarding failure must never surface elsewhere.
		try {
			sendToAdobe(experimentName);
		} catch {
			/* swallow */
		}
	};

	return {
		add(item: DecisionListItem) {
			const formatted = formatDecision(item);
			if (sentAssignments.has(formatted)) return;
			sentAssignments.add(formatted);
			pending.set(formatted, item);
			// Fixed window from the first item, not a resetting debounce.
			if (timer === null) {
				timer = setTimeout(flush, GRACE_MS);
			}
		},
	};
}

// Page-level singleton, mirrors client-singleton.ts. Timer lives here, so it survives a provider unmount.
export function getForwardingBuffer(): ForwardingBuffer | null {
	if (typeof window === 'undefined') return null;
	if (!window.__fshrForwardingBuffer) {
		window.__fshrForwardingBuffer = createForwardingBuffer();
	}
	return window.__fshrForwardingBuffer;
}
